// Caché de lecturas offline (F4 #51). Persiste la caché de TanStack Query en
// IndexedDB (vía Dexie) para hidratar la UI sin red al reabrir la app en planta.
//
// Principios (PLAN.md §Sincronización — lecturas):
//   - Se persiste SOLO estado operativo (allowlist explícita, fail-safe): árbol de
//     activos, candados, procedimientos, LOTOs activos + bloqueos, panel y permisos
//     del usuario. Nunca PII ampliada, auditoría, NDA ni usuarios/roles.
//   - Purga total de la caché en logout / sesión caducada (y, transitivamente, en
//     cambio de usuario: para autenticarse como otro hay que pasar antes por uno de
//     esos dos eventos). Aísla los datos entre usuarios de una tablet compartida.
//   - TTL de la caché (maxAge) + poda de la ficha de LOTOs ya cerrados.
//
// La caché de lecturas vive en una DB Dexie DEDICADA (`loto-cache`): la outbox de
// mutaciones (#52) usará su propia DB, de modo que purgar las lecturas nunca
// descarta el trabajo encolado sin red.

import type { Query } from "@tanstack/react-query";
import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";
import Dexie, { type Table } from "dexie";

/**
 * CACHE_BUSTER versiona la forma de lo persistido. Al cambiar la allowlist o el
 * esquema de los datos operativos, súbelo: persistQueryClient descartará cualquier
 * caché previa cuyo buster no coincida en vez de rehidratar datos incompatibles.
 */
// #435: `me` gana `documentacion_acreditativa_requerida` en cada obra (antes: "abierto-por-v1").
export const CACHE_BUSTER = "documentacion-acreditativa-v1";

/**
 * CACHE_MAX_AGE_MS es el TTL de la caché persistida. Pasado este tiempo sin
 * refrescar, la rehidratación se descarta (los datos operativos no deben servirse
 * indefinidamente sin red). El gcTime por defecto del QueryClient se alinea a este
 * valor para que las queries sobrevivan en memoria el tiempo suficiente a persistir.
 */
export const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 h

/**
 * CACHEABLE_ROOTS es la allowlist de raíces operativas POR obra que SÍ se persisten
 * (F7 #159). Las queries de obra viajan namespaceadas como `["obra", obraId, root, …]`,
 * así que la allowlist se aplica al TERCER elemento (`key[2]`). Es una lista de inclusión
 * a propósito (fail-safe para la confidencialidad): cualquier query de obra cuyo `root` no
 * esté aquí se queda siempre online y jamás toca el disco. `me` (identidad + gates) se
 * persiste aparte por ser global, no de obra.
 */
const CACHEABLE_ROOTS: ReadonlySet<string> = new Set([
  "areas",
  "equipos",
  "fuentes",
  "puntos",
  "candados",
  "procedimientos",
  "lotos", // índice + ficha (la ficha de CERRADO se poda; ver abajo)
  "dashboard", // panel operativo (KPIs, candados activos, LOTOs recientes)
]);

/**
 * shouldPersistQuery decide qué queries se vuelcan a IndexedDB. Persiste `me` (global) y
 * las queries operativas de obra (`["obra", obraId, root, …]`) cuya raíz esté en la
 * allowlist, resueltas con éxito; poda la ficha de LOTOs ya cerrados (no son operativos:
 * el histórico se consulta online). El índice de LOTOs (clave sin id) se conserva entero
 * porque no puede deshidratarse parcialmente; su frescura la acota el TTL.
 */
export function shouldPersistQuery(query: Query): boolean {
  if (query.state.status !== "success") {
    return false;
  }
  const [root, , obraRoot, cuarto] = query.queryKey as ReadonlyArray<unknown>;
  // `me` es global (identidad básica + gates del usuario): se persiste con su clave raíz.
  if (root === "me") {
    return true;
  }
  // El resto de estado operativo va namespaceado por obra: ["obra", obraId, root, …].
  if (root !== "obra" || typeof obraRoot !== "string" || !CACHEABLE_ROOTS.has(obraRoot)) {
    return false;
  }
  // Listas FILTRADAS (#247, D8): la clave `["obra", obraId, root, {filtros}]` es una VISTA
  // derivada y volátil de la lista base. No se persiste —multiplicaría copias parciales en
  // disco y quedarían desincronizadas—: sin red se filtra sobre la lista base con la
  // función pura de la entidad. Solo la base y la ficha por id numérico llegan a IndexedDB.
  if (typeof cuarto === "object" && cuarto !== null) {
    return false;
  }
  // Poda de LOTOs cerrados: la ficha ["obra", obraId, "lotos", id] de un LOTO CERRADO no se
  // persiste; el índice (sin id, `cuarto` undefined) sí, entero.
  if (obraRoot === "lotos" && cuarto !== undefined) {
    const data = query.state.data as { estado?: string } | undefined;
    if (data?.estado === "CERRADO") {
      return false;
    }
  }
  return true;
}

// --- DB Dexie dedicada a la caché de lecturas -----------------------------------

type CacheRow = { key: string; value: PersistedClient };

class OfflineCacheDB extends Dexie {
  cache!: Table<CacheRow, string>;

  constructor() {
    super("loto-cache");
    this.version(1).stores({ cache: "key" });
  }
}

const db = new OfflineCacheDB();

// Clave única bajo la que persistQueryClient guarda el cliente deshidratado.
const PERSISTED_CLIENT_KEY = "reactQueryClient";

/**
 * offlinePersister implementa el contrato Persister de persistQueryClient sobre la
 * DB Dexie: guarda/restaura/borra el cliente deshidratado como un único registro.
 */
export const offlinePersister: Persister = {
  persistClient: async (client: PersistedClient): Promise<void> => {
    await db.cache.put({ key: PERSISTED_CLIENT_KEY, value: client });
  },
  restoreClient: async (): Promise<PersistedClient | undefined> => {
    const row = await db.cache.get(PERSISTED_CLIENT_KEY);
    return row?.value;
  },
  removeClient: async (): Promise<void> => {
    await db.cache.delete(PERSISTED_CLIENT_KEY);
  },
};

/**
 * purgeOfflineCache vacía la caché de lecturas en disco. Se invoca en logout y ante
 * sesión caducada (401) para garantizar que ningún dato operativo de un usuario
 * sobreviva a otro en una tablet compartida. Es best-effort: un fallo al purgar no
 * debe romper el logout ni la redirección de sesión caducada.
 *
 * NOTA: no descarta la outbox de mutaciones (#52), que reside en su propia DB.
 */
export async function purgeOfflineCache(): Promise<void> {
  try {
    await db.cache.clear();
  } catch {
    // Best-effort: el aislamiento real lo refuerzan logout + 401; un fallo puntual
    // de IndexedDB no debe bloquear al usuario.
  }
}

/**
 * requestPersistentStorage pide al navegador almacenamiento persistente para que la
 * caché offline no sea desalojada bajo presión de disco. Best-effort y con
 * feature-detection: en navegadores sin la API simplemente devuelve false.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.storage?.persist) {
      return await navigator.storage.persist();
    }
  } catch {
    // Ignorado: es una optimización, no un requisito de correctitud.
  }
  return false;
}
